import React from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import SEOHead from "../../components/SEOHead";
import { Shield, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";

const industrialPipeline = "/images/industrial-pipeline.jpeg";
const oilPlatforms = "/images/oil-platforms.jpeg";

export default function Leasing() {
  return (
    <div className="min-h-screen bg-gray-950">
      <SEOHead
        title="Equipment Leasing Services - Alkane Engineering Limited"
        description="Flexible equipment leasing solutions tailored to project-specific requirements. Cost-effective leasing options in Nigeria."
        keywords="equipment leasing, rental services, heavy equipment, machinery leasing, cost-effective solutions"
        canonical="https://alkanesengineering.com/services/leasing"
      />
      <Header />

      <section
        className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-28"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-primary mb-6 hover:gap-3 transition-all"
          >
            <ArrowLeft size={20} /> Back to Services
          </Link>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-white">
            Equipment Leasing
          </h1>
          <p className="text-lg text-gray-100 max-w-2xl">
            Flexible equipment leasing solutions tailored to project-specific
            requirements.
          </p>
        </div>
      </section>

      <section
        className="relative py-16 md:py-24"
        style={{
          backgroundImage: `url(${industrialPipeline})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gray-950/85"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-400 text-white flex-shrink-0">
                  <Shield size={32} />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    Equipment Leasing
                  </h2>
                  <p className="text-gray-300">Flexible equipment solutions</p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Overview</h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Flexible leasing options for specialized equipment and
                  machinery, eliminating capital expenditure while ensuring
                  access to modern, well-maintained equipment. Perfect for
                  temporary project requirements.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Key Capabilities
                </h3>
                <ul className="space-y-3">
                  {[
                    "Heavy machinery rental",
                    "Specialized equipment leasing",
                    "Maintenance and servicing included",
                    "Flexible lease terms",
                    "Equipment replacement guarantee",
                    "Full insurance coverage",
                  ].map((capability, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-gray-300"
                    >
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{capability}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Sectors Served
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["Oil & Gas", "Power Generation", "Infrastructure"].map(
                    (sector, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 rounded-full bg-primary/20 text-primary font-semibold text-sm border border-primary/40"
                      >
                        {sector}
                      </span>
                    ),
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl border-2 border-primary/30 p-8 h-fit">
              <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center mx-auto">
                  <Shield size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">
                  Need Equipment?
                </h3>
                <p className="text-gray-300">
                  Flexible leasing for all your equipment needs.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 w-full bg-primary text-white px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-1"
                >
                  Get Lease Quote <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="relative text-white py-16 md:py-24"
        style={{
          backgroundImage: `url(${oilPlatforms})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary/85"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold text-white">
            Lease Modern, Well-Maintained Equipment
          </h2>
          <p className="text-lg text-gray-100 max-w-2xl mx-auto">
            Save on capital costs with our flexible leasing options.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:shadow-xl hover:-translate-y-1 active:scale-95"
          >
            Request Leasing Proposal
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
